import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Copy, FileDown, Map, Clock } from 'lucide-react';

const Toolbar = ({
  projects,
  selectedProject,
  setSelectedProject,
  selectedDate,
  setSelectedDate,
  handleDateChange,
  copyTasksToNextDay,
  setShowExportModal,
  setShowLiveMap,
  showLiveMap,
  setShowTimeline,
  showTimeline
}) => {
  const dateInputRef = useRef(null);

  // Format date for input
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Open date picker programmatically
  const openDatePicker = () => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker();
    }
  };
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white border border-border rounded-lg shadow-sm mb-3">
      {/* Left side - Project & Date */}
      <div className="flex items-center gap-4">
        {/* Project Select */}
        <div className="flex items-center gap-2">
          <label htmlFor="project-select" className="text-sm font-medium text-muted-foreground">
            Project
          </label>
          <select
            id="project-select"
            className="h-9 pl-3 pr-8 py-1 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 cursor-pointer max-w-[250px] truncate"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
          >
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        {/* Date Navigation */}
        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
          <button
            onClick={() => handleDateChange('prev')}
            className="p-1.5 rounded-md hover:bg-background hover:shadow-sm transition-all text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft size={18} />
          </button>
          <div
            onClick={openDatePicker}
            className="flex items-center gap-2 px-3 py-1 hover:bg-background rounded-md cursor-pointer transition-all"
          >
            <Calendar size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{selectedDate.toDateString()}</span>
            <input
              ref={dateInputRef}
              type="date"
              value={formatDateForInput(selectedDate)}
              onChange={(e) => {
                if (e.target.value && setSelectedDate) {
                  setSelectedDate(new Date(e.target.value + 'T12:00:00'));
                }
              }}
              className="sr-only"
            />
          </div>
          <button
            onClick={() => handleDateChange('next')}
            className="p-1.5 rounded-md hover:bg-background hover:shadow-sm transition-all text-muted-foreground hover:text-foreground"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Right side - Action Buttons */}
      <div className="flex items-center gap-2">
        {/* Copy Tasks */}
        <button
          onClick={copyTasksToNextDay}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-border bg-background text-foreground hover:bg-muted transition-colors"
        >
          <Copy size={14} />
          <span className="hidden lg:inline">Copy Incomplete</span>
        </button>

        {/* Export */}
        <button
          onClick={() => setShowExportModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-border bg-background text-foreground hover:bg-muted transition-colors"
        >
          <FileDown size={14} />
          <span className="hidden lg:inline">Export</span>
        </button>

        {/* Divider */}
        <div className="w-px h-6 bg-border mx-1" />

        {/* Live Map Toggle */}
        <button
          onClick={() => setShowLiveMap(!showLiveMap)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            showLiveMap
              ? 'bg-violet-100 text-violet-700 border border-violet-200 hover:bg-violet-200'
              : 'border border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <Map size={14} />
          <span className="hidden lg:inline">Map</span>
        </button>

        {/* Timeline Toggle */}
        <button
          onClick={() => setShowTimeline(!showTimeline)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            showTimeline
              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-200'
              : 'border border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
        >
          <Clock size={14} />
          <span className="hidden lg:inline">Timeline</span>
        </button>
      </div>
    </div>
  );
};

export default Toolbar;
